import { chrome } from '@/lib/content';
import { scrollToContactBand } from '@/lib/contact-scroll';
import { getMedicalOfficeRoute } from '@/lib/medical-family-office';
import { useLocation } from 'wouter';
import CountrySwitcher from './CountrySwitcher';
import { isGermanyPath, SWISS_ORIGIN } from '@/lib/markets';
import SmartLink from './SmartLink';
import '@/styles/medical-office-chrome.css';

type OfficeRoute = NonNullable<ReturnType<typeof getMedicalOfficeRoute>>;

export function OfficeLanguageSwitcher({ page }: { page?: OfficeRoute }) {
  const [location] = useLocation();
  const currentLanguage = page?.language ?? 'de';
  return (
    <nav className="hp-language-switch" aria-label={currentLanguage === 'en' ? 'Language' : 'Sprache'}>
      {(['de', 'en'] as const).map((language) =>
        language === currentLanguage ? (
          <span key={language} aria-current="page" lang={language}>{language.toUpperCase()}</span>
        ) : (
          <SmartLink key={language} to={page?.pairedRoute ?? `${isGermanyPath(location) ? SWISS_ORIGIN : ''}/en/private-medical-office`} hrefLang={language} lang={language}
            aria-label={language === 'en' ? 'English' : 'Deutsch'}>
            {language.toUpperCase()}
          </SmartLink>
        ),
      )}
    </nav>
  );
}

export function EnglishOfficeHeader({ page }: { page: OfficeRoute }) {
  return (
    <header className="hp-header hp-office-header" lang="en">
      <div className="hp-container hp-office-header__inner">
        <SmartLink to="/" className="hp-header__brand" aria-label="Health Prime home">
          <img className="hp-header__logo" src={chrome.brand.logo} alt={chrome.brand.name} width={778} height={124} />
        </SmartLink>
        <p className="hp-office-header__title">{page.label}</p>
        <OfficeLanguageSwitcher page={page} />
        <a className="hp-button hp-button--primary hp-button--sm" href="/#kontakt-aufnehmen" onClick={scrollToContactBand}>Request a confidential call</a>
      </div>
    </header>
  );
}

export function EnglishOfficeFooter() {
  return (
    <footer className="hp-footer hp-office-footer" id="footer-kontakt" lang="en">
      <div className="hp-container hp-office-footer__inner">
        <div className="hp-stack">
          <SmartLink to="/" aria-label="Health Prime home">
            <img className="hp-footer__logo" src={chrome.brand.logo} alt={chrome.brand.name} width={778} height={124} />
          </SmartLink>
          <p>Health Prime Medical Family Office<br />A department of Health Prime AG<br />Samstagernstrasse 55<br />8832 Wollerau</p>
          <a href="tel:+41442502872">+41 44 250 28 72</a>
          <a href={chrome.contact.whatsapp} target="_blank" rel="noopener noreferrer">WhatsApp Chat</a>
        </div>
        <nav aria-label="Medical Family Office services" className="hp-stack">
          <SmartLink to="/en/executive-medical-office">Executive Medical Office</SmartLink>
          <SmartLink to="/en/private-medical-office">Private Medical Office</SmartLink>
        </nav>
        <div className="hp-stack">
          <h2 className="hp-h6">Facts &amp; Figures</h2>
          <SmartLink to="/bgm-zahlen-schweiz">BGM figures</SmartLink>
          <SmartLink to="/business-case-bgm">Business case</SmartLink>
          <CountrySwitcher />
          <SmartLink to="/impressum" hrefLang="de">Legal notice (in German)</SmartLink>
          <SmartLink to="/datensicherheit" hrefLang="de">Privacy notice (in German)</SmartLink>
        </div>
      </div>
    </footer>
  );
}
